import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Linkedin, Twitter, Facebook, Instagram, Pencil, FileText, CalendarDays, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { fileUrl } from "@/lib/api";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { VideoEmbed } from "@/components/common/VideoEmbed";
import { Paygate } from "@/components/common/Paygate";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { FanFollowButtons, BookmarkButton, MessageButton } from "@/components/social/ProfileActions";
import { ReportMenu } from "@/components/social/ReportMenu";
import { ShareProfileButton } from "@/components/social/ShareProfileButton";
import { OrgFollowCounts } from "@/components/social/PeopleListModal";
import { InquiryButton } from "@/components/social/InquiryDialog";
import { PitchDeckButton } from "@/components/social/PitchDeckDialog";
import { CategoryBadge } from "@/components/common/CategoryBadge";
import { AskQuestionButton } from "@/components/social/AskQuestionDialog";
import { ChatterFeed } from "@/components/social/ChatterFeed";
import { FundingHistory, money, monthYear } from "@/components/profile/FundingHistory";
import { PublicQna } from "@/components/social/PublicQna";
import { trackOutboundLink, trackInternalLink } from "@/lib/analytics";

const loc = (l) => [l?.city, l?.state, l?.country].filter(Boolean).join(", ");
const SOCIALS = [["linkedin", Linkedin], ["x", Twitter], ["facebook", Facebook], ["instagram", Instagram]];
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch (e) { return ""; } };
const MAX_INDUSTRIES = 6;

const fmtAddress = (a) => {
  if (!a) return "";
  const stateZip = [a.state, a.zip].filter(Boolean).join(" ");
  const parts = [a.line1, a.line2, a.city, stateZip].map((s) => (s || "").trim()).filter(Boolean);
  // A lone country isn't a real address — don't render a phantom address block.
  if (parts.length === 0) return "";
  return [...parts, (a.country || "").trim()].filter(Boolean).join(", ");
};
const mapsHref = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const withProto = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
const displayUrl = (u) => (u || "").replace(/^https?:\/\//i, "").replace(/\/+$/, "");

function Financials({ o, references, kind, locked }) {
  const ent = kind === "capital" ? "capital.financials.view" : "business.financials.view";
  const f = o.financials || {};
  const history = f.funding_history || [];
  const totals = f.raised_totals || [];
  const cur = f.financial_currency || "USD";
  // Once structured funding history exists it is the source for Total raised; otherwise the
  // legacy "Amount raised" range keeps showing exactly as before.
  const legacyRaised = (f.amount_raised?.value ? labelFor(references.amount_raised_ranges, f.amount_raised.value) : "")
    + (f.amount_raised?.value && f.amount_raised?.as_of ? ` (as of ${f.amount_raised.as_of})` : "");
  const totalRaised = history.length
    ? totals.map((t) => `${money(t.total, t.currency)} ${t.currency}`).join(" · ")
    : legacyRaised;
  const arr = (f.arr?.value ? labelFor(references.arr_ranges, f.arr.value) : "")
    + (f.arr?.value && f.arr?.as_of ? ` (as of ${monthYear(f.arr.as_of)})` : "");
  // Optional fields are simply omitted — no empty rows.
  const rows = [
    ["Revenue status", labelFor(references.revenue_status, f.revenue_status)],
    ["ARR", arr],
    ["MRR", f.mrr != null ? money(f.mrr, cur) : ""],
    ["Monthly burn", f.monthly_burn != null ? money(f.monthly_burn, cur) : ""],
    ["Runway", f.runway_months != null ? `${f.runway_months} months` : ""],
    ["Total raised", totalRaised],
  ].filter(([, v]) => v);
  const placeholder = [["Revenue status", "Post-Revenue"], ["ARR", "$250k-$500k"], ["Total raised", "$1.25M USD"]];
  const data = locked ? placeholder : rows;
  if (!locked && !rows.length && !history.length) return null;
  return (
    <Card className="border-0 p-6" data-testid="org-financials-card">
      <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Financials</h3>
      <div className="mt-3">
        <Paygate entitlement={ent} title="Financials are locked" body="Upgrade your plan to view this organization's financials." minHeight={150}>
          <dl className="space-y-2.5">
            {data.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-2 text-sm last:border-0"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-[540] text-foreground">{v}</dd></div>
            ))}
          </dl>
          {!locked && <FundingHistory entries={history} references={references} />}
        </Paygate>
      </div>
    </Card>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border/60 pb-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right font-[540] text-foreground">{children}</dd>
    </div>
  );
}

function CompanyDetails({ o, references, kind }) {
  const contact = o.contact || {};
  const social = o.social || {};
  // Outbound click tracking (Phase 4): bounded link kind + destination host only.
  const outbound = (linkKind, url) => trackOutboundLink(kind, o.id, linkKind, url);
  const rows = [];
  if (o.founded_year) rows.push(["Founded", String(o.founded_year)]);
  if (o.stage && kind !== "capital" && (o.startup_mode ?? true)) rows.push(["Stage", labelFor(references.funding_stages, o.stage)]);
  if (o.company_size) rows.push(["Company size", `${labelFor(references.company_sizes, o.company_size)} people`]);
  const location = loc(o.location);
  if (location) rows.push(["Location", location]);
  const classifications = (o.business_classifications || []).map((c) => labelFor(references.business_classifications, c)).filter(Boolean);
  const socials = SOCIALS.filter(([k]) => social[k]);
  const physAddr = fmtAddress(o.physical_address);
  const mailAddr = fmtAddress(o.mailing_address);
  // Per spec — only surface the address rows when an actual street address is
  // provided (line1). Without line1 the values would duplicate the Location
  // row above, so we suppress them entirely.
  const physHasStreet = Boolean((o.physical_address?.line1 || "").trim());
  const mailHasStreet = Boolean((o.mailing_address?.line1 || "").trim());
  const showPhys = physAddr && physHasStreet;
  const showMail = mailAddr && mailHasStreet && mailAddr !== physAddr;

  if (rows.length === 0 && classifications.length === 0 && !contact.website && !contact.email && !contact.phone && !showPhys && !showMail && socials.length === 0) return null;

  return (
    <Card className="border-0 p-6" data-testid="org-company-details-card">
      <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Company Details</h3>
      <dl className="mt-3 space-y-2.5 text-sm">
        {rows.map(([k, v]) => <DetailRow key={k} label={k}>{v}</DetailRow>)}
        {contact.website && <DetailRow label="Website"><a href={withProto(contact.website)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="org-company-website-link" onClick={() => outbound("website", withProto(contact.website))}>{displayUrl(contact.website)}</a></DetailRow>}
        {contact.email && <DetailRow label="Email"><a href={`mailto:${contact.email}`} className="text-primary hover:underline" data-testid="org-company-email" onClick={() => outbound("email")}>{contact.email}</a></DetailRow>}
        {contact.phone && (() => {
          // Build a tel: href by stripping everything that isn't a + or a digit.
          // Display stays exactly as the user typed it (per spec).
          const raw = `${contact.phone_country_code || ""}${contact.phone}`;
          const tel = `tel:${raw.replace(/[^+\d]/g, "")}`;
          return (
            <DetailRow label="Phone">
              <a href={tel} className="text-primary hover:underline" data-testid="org-company-phone" onClick={() => outbound("phone")}>
                {contact.phone_country_code} {contact.phone}
              </a>
            </DetailRow>
          );
        })()}
        {showPhys && (
          <div className="flex justify-between gap-3 border-b border-border/60 pb-2 last:border-0">
            <dt className="shrink-0 text-muted-foreground">Address</dt>
            <dd className="text-right"><a href={mapsHref(physAddr)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="org-company-physical-address" onClick={() => outbound("map", mapsHref(physAddr))}>{physAddr}</a></dd>
          </div>
        )}
        {showMail && (
          <div className="flex justify-between gap-3 border-b border-border/60 pb-2 last:border-0">
            <dt className="shrink-0 text-muted-foreground">Mailing</dt>
            <dd className="text-right"><a href={mapsHref(mailAddr)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="org-company-mailing-address" onClick={() => outbound("map", mapsHref(mailAddr))}>{mailAddr}</a></dd>
          </div>
        )}
      </dl>
      {classifications.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-[600] text-muted-foreground">Classifications</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {classifications.map((c) => <Badge key={c} variant="secondary">{c}</Badge>)}
          </div>
        </div>
      )}
      {socials.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-[600] text-muted-foreground">Social</div>
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid="org-company-social">
            {socials.map(([k, Icon]) => (
              <a key={k} href={social[k]} target="_blank" rel="noreferrer" aria-label={k} title={k} className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-primary" onClick={() => outbound(`social_${k}`, social[k])}><Icon className="h-4 w-4" /></a>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function LinkList({ title, items, to, Icon, sub, testid, onItemClick }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="border-0 p-6" data-testid={testid}>
      <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">{title}</h3>
      <div className="mt-3 space-y-2.5">
        {items.map((it) => (
          <Link key={it.id} to={to(it)} className="flex items-center gap-3 hover:opacity-80" data-testid={`${testid}-${it.slug}`} onClick={onItemClick ? () => onItemClick(it) : undefined}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-secondary text-muted-foreground"><Icon className="h-4 w-4" /></div>
            <div className="min-w-0">
              <div className="truncate text-sm font-[560] text-foreground">{it.title}</div>
              {sub(it) && <div className="truncate text-xs text-muted-foreground">{sub(it)}</div>}
            </div>
          </Link>
        ))}
      </div>
    </Card>
  );
}

export function OrgProfileView({ org: o, kind }) {
  const { references } = useReferences();
  const isCapital = kind === "capital";
  const inv = o.investment_focus || {};
  const show = (v) => v && (Array.isArray(v) ? v.length > 0 : true);
  // check_size may be a single legacy value or an array (multi-select).
  const csizes = [].concat(inv.check_size || []).filter(Boolean);
  const meetingType = isCapital ? "capital_meeting" : "business_meeting";
  const pitchType = isCapital ? "capital_pitchdeck" : "business_pitchdeck";

  const hasAbout = show(o.about) && o.about !== "<p></p>";
  // Public Links: an unauthenticated visitor gets the same layout, read-only — every
  // member-only interaction is withheld rather than rendered and then refused.
  const publicMode = o.is_public_view === true;
  const hasSolving = show(o.solving) && o.solving !== "<p></p>";
  const hasGoodFit = isCapital && show(o.good_fit) && o.good_fit !== "<p></p>";
  const hasIndustries = show(o.industries);
  const industries = o.industries || [];

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4">
          <Link to={isCapital ? "/capital" : "/businesses"} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="org-back-link"><ArrowLeft className="h-4 w-4" /> {isCapital ? "All capital" : "All businesses"}</Link>
        </div>
        {/* Cover (borderless, contained to profile width) */}
        <div className="relative mt-3 h-40 w-full overflow-hidden bg-gradient-to-r from-teal-100 to-teal-50 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.25)] sm:h-56">
          {o.cover_url && <img src={fileUrl(o.cover_url)} alt="cover" className="h-full w-full object-cover" />}
          {(o.can_edit ?? o.is_owner) && (
            <Link to={isCapital ? `/dashboard/capital-profiles/${o.id}` : `/dashboard/businesses/${o.id}`} className="absolute right-3 top-3" data-testid="org-edit-button">
              <Button size="sm" variant="secondary" className="shadow-sm"><Pencil className="mr-1.5 h-4 w-4" /> Edit profile</Button>
            </Link>
          )}
        </div>

        {/* Phones: header → Get in touch → About/People/Q&A → Chatter last (it can scroll
            forever). `contents` drops the left column into the page flow so ordering works. */}
        <div className="mt-6 flex flex-col gap-6 pb-16 lg:grid lg:grid-cols-12 lg:gap-6">
          {/* LEFT (wide) */}
          <div className="contents lg:block lg:space-y-6 lg:col-span-8">
            <Card className="relative order-1 border-0 p-6 lg:order-none">
              <div className="absolute right-4 top-4 z-10 flex items-center gap-0.5">
                <ShareProfileButton handle={o.handle} slug={o.slug} name={o.display_name}
                  basePath={isCapital ? "/capital" : "/b"}
                  subject={isCapital ? "capital profile" : "business"}
                  updatePath={`/orgs/${kind}/${o.id}`}
                  isOwner={o.is_owner === true} publicLinkEnabled={o.public_link_enabled === true} />
                {!o.is_owner && !publicMode && (
                  <>
                    <BookmarkButton itemType={kind} itemId={o.id} iconOnly variant="ghost" />
                    <ReportMenu contentType={kind} contentId={o.id} ownerUserId={o.owner_id} isOwner={o.is_owner} testId="report-menu-org" />
                  </>
                )}
              </div>
              {/* Phones get the tagline/location/category on their own full-width row under
                  the logo — beside a 150px logo they were squeezed into a sliver. */}
              <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-x-4 gap-y-3 sm:grid-cols-[150px_minmax(0,1fr)]">
                <div className="h-20 w-20 shrink-0 overflow-hidden border-4 border-card bg-secondary shadow-sm sm:row-span-2 sm:h-[150px] sm:w-[150px]">
                  {o.profile_image_url ? <img src={fileUrl(o.profile_image_url)} alt={o.display_name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-4xl text-muted-foreground">{(o.display_name || "?")[0]}</div>}
                </div>
                <div className="min-w-0 pt-1 pr-12">
                  <h1 className="text-xl font-[700] text-foreground sm:text-2xl">{o.display_name}</h1>
                  {o.handle && <Link to={`/@${o.handle}`} className="text-sm font-[500] text-primary hover:underline" data-testid="org-handle-link">@{o.handle}</Link>}
                </div>
                <div className="col-span-2 min-w-0 sm:col-span-1 sm:col-start-2 sm:-mt-1" data-testid="org-meta">
                  {o.headline && <p className="text-foreground">{o.headline}</p>}
                  {loc(o.location) && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" /> {loc(o.location)}
                    </div>
                  )}
                  {!isCapital && o.profile_category && (
                    <div className="mt-2"><CategoryBadge value={o.profile_category} references={references} testId="org-category-badge" /></div>
                  )}
                  {isCapital && o.capital_type && (
                    <div className="mt-3">
                      <Badge className="bg-teal-50 text-teal-700">{labelFor(references.capital_types, o.capital_type)}</Badge>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Video above About */}
            {o.intro_video?.url && <Card className="order-3 border-0 p-6 lg:order-none" data-testid="org-video-card"><VideoEmbed video={o.intro_video} /></Card>}

            {/* About + What we're solving + Industries — one card */}
            {(hasAbout || hasSolving || hasGoodFit || hasIndustries) && (
              <Card className="order-3 border-0 p-6 lg:order-none" data-testid="org-about-card">
                {hasAbout && (
                  <>
                    <h3 className="text-lg font-[640] text-primary">About</h3>
                    <div className="mt-3"><RichTextView html={o.about} className="prose-so-spacious leading-[1.15rem]" /></div>
                  </>
                )}
                {hasSolving && (
                  <div className={hasAbout ? "mt-6" : ""}>
                    <h3 className="text-lg font-[640] text-primary">What we&apos;re solving</h3>
                    <div className="mt-3"><RichTextView html={o.solving} className="prose-so-spacious leading-[1.15rem]" /></div>
                  </div>
                )}
                {hasGoodFit && (
                  <div className={(hasAbout || hasSolving) ? "mt-6" : ""}>
                    <h3 className="text-lg font-[640] text-primary">We&apos;re a good fit for…</h3>
                    <div className="mt-3"><RichTextView html={o.good_fit} className="prose-so-spacious leading-[1.15rem]" /></div>
                  </div>
                )}
                {hasIndustries && (
                  <div className={(hasAbout || hasSolving) ? "mt-6" : ""}>
                    <h3 className="text-lg font-[640] text-primary">Industries</h3>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {industries.slice(0, MAX_INDUSTRIES).map((i) => <Badge key={i} variant="secondary">{labelFor(references.industries, i)}</Badge>)}
                      {industries.length > MAX_INDUSTRIES && <Badge variant="secondary" data-testid="org-more-industries">+{industries.length - MAX_INDUSTRIES}</Badge>}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {show(o.people) && (
              <Card className="order-3 border-0 p-6 lg:order-none" data-testid="org-people-card">
                <h3 className="text-lg font-[640] text-primary">People</h3>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {o.people.map((p, i) => {
                    const inner = (
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          {p.headshot_url && <AvatarImage src={fileUrl(p.headshot_url)} alt="" className="object-cover" />}
                          <AvatarFallback className="bg-teal-50 text-teal-700">{(p.display_name || "?")[0]}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-[560] text-foreground">{p.display_name}</div>
                          {p.role && <div className="truncate text-xs text-muted-foreground">{p.role}</div>}
                        </div>
                      </div>
                    );
                    return p.slug ? (
                      <Link key={p.slug} to={`/p/${p.slug}`} className="transition-opacity hover:opacity-80" data-testid={`org-person-${p.slug}`} onClick={() => trackInternalLink(kind, o.id, "team_member")}>{inner}</Link>
                    ) : (
                      <div key={`${p.display_name}-${i}`}>{inner}</div>
                    );
                  })}
                </div>
              </Card>
            )}

            {!publicMode && <div className="order-3 lg:order-none"><PublicQna targetType={kind} targetId={o.id} /></div>}
            {!publicMode && (
              <div className="order-4 lg:order-none" data-testid="org-chatter-section">
                <ChatterFeed profileType={kind} profileId={o.id} ownerName={o.display_name} />
              </div>
            )}
          </div>

          {/* RIGHT (slim) */}
          <div className="order-2 space-y-6 lg:order-none lg:col-span-4">
            {publicMode && (
              <Card className="border-0 p-6" data-testid="public-profile-join-cta">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Get in touch</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  Introductions, meeting requests and pitch decks go through Startup Otter. Create a free
                  account to reach {o.display_name}.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/register"><Button className="rounded-full" data-testid="public-profile-join-button">Join Startup Otter</Button></Link>
                  <Link to={`/login?next=${isCapital ? "/capital" : "/b"}/${o.slug}`}>
                    <Button variant="ghost" className="rounded-full" data-testid="public-profile-signin-button">Sign in to connect</Button>
                  </Link>
                </div>
              </Card>
            )}

            {!o.is_owner && !publicMode && (
              <Card className="border-0 p-6">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Get in touch</h3>
                <div className="mt-3 space-y-2">
                  {(o.show_pitchdeck_cta ?? true) && (isCapital
                    ? <PitchDeckButton capitalId={o.id} capitalName={o.display_name} />
                    : <InquiryButton type={pitchType} targetId={o.id} targetName={o.display_name} primary />)}
                  {(o.show_meeting_cta ?? true) && <InquiryButton type={meetingType} targetId={o.id} targetName={o.display_name} />}
                  <AskQuestionButton targetType={kind} targetId={o.id} targetName={o.display_name} />
                  {/* Temporarily hidden per request — do not delete/change routing */}
                  {false && o.owner_id && <MessageButton targetUserId={o.owner_id} variant="secondary" label="Message owner" className="w-full justify-start" />}
                </div>
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <FanFollowButtons targetType={kind} targetId={o.id} order={["follow", "fan"]} showCount={false} className="w-full justify-center" />
                  </div>
                  <OrgFollowCounts targetType={kind} targetId={o.id} targetName={o.display_name} className="justify-center pt-1" />
                </div>
              </Card>
            )}

            <CompanyDetails o={o} references={references} kind={kind} />

            {isCapital && (show(inv.industries) || show(inv.stages) || csizes.length > 0) && (
              <Card className="border-0 p-6" data-testid="org-investment-focus-card">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-primary">Investment Focus</h3>
                <div className="mt-4 space-y-4">
                  {show(inv.industries) && <div><div className="text-sm font-[600] text-muted-foreground">Industries</div><div className="mt-1.5 flex flex-wrap gap-1.5">{inv.industries.map((i) => <Badge key={i} className="bg-teal-50 text-teal-700">{labelFor(references.industries, i)}</Badge>)}</div></div>}
                  {show(inv.stages) && <div><div className="text-sm font-[600] text-muted-foreground">Stages</div><div className="mt-1.5 flex flex-wrap gap-1.5">{inv.stages.map((s) => <Badge key={s} variant="secondary">{labelFor(references.funding_stages, s)}</Badge>)}</div></div>}
                  {csizes.length > 0 && <div><div className="text-sm font-[600] text-muted-foreground">Check size</div><div className="mt-1.5 flex flex-wrap gap-1.5">{csizes.map((c) => <Badge key={c} variant="secondary">{labelFor(references.check_sizes, c)}</Badge>)}</div></div>}
                </div>
              </Card>
            )}

            {kind !== "capital" && !publicMode && (o.startup_mode ?? true) && (
              <Financials o={o} references={references} kind={kind} locked={o.financials_locked} />
            )}

            <LinkList
              title="Resources contributed"
              items={o.contributed_resources}
              to={(r) => `/resource/${r.slug}`}
              Icon={FileText}
              sub={(r) => labelFor(references.resource_types, r.resource_type)}
              testid="org-resources-contributed"
              onItemClick={(r) => trackInternalLink(kind, o.id, "resource", "resource", r.id)}
            />

            <LinkList
              title="Events planned"
              items={o.planned_events}
              to={(e) => `/event/${e.slug}`}
              Icon={CalendarDays}
              sub={(e) => fmtDate(e.start_at)}
              testid="org-events-planned"
              onItemClick={(e) => trackInternalLink(kind, o.id, "event", "event", e.id)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
